/**
 * Product Library Page
 *
 * Displays the tenant product catalog with import and management capabilities.
 * Products are imported from UmageScraper JSON output.
 * Owner-only access.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from '../../components/TopBar';
import { useTenant } from '../../contexts/TenantContext';
import { productService, ProductImportResult } from '../../services/productService';
import { Product } from '../../contexts/StudioContext';


interface ProductLibraryProps {
  tenantId: string;
  apiBaseUrl: string;
  authToken: string;
}

export const ProductLibrary: React.FC<ProductLibraryProps> = ({ tenantId, apiBaseUrl, authToken }) => {
  const navigate = useNavigate();
  const { currentTenant, currentUser } = useTenant();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ProductImportResult | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [classificationFilter, setClassificationFilter] = useState('');

  // Selected product for details panel
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // File input for import
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    productService.setAccessToken(authToken);
  }, [authToken]);

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, searchQuery, classificationFilter]);

  const fetchProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await productService.listProducts({
        tenantId,
        search: searchQuery || undefined,
        classification: classificationFilter || undefined,
      });
      setProducts(response.products || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportResult(null);
    setError(null);

    try {
      const text = await file.text();
      let parsed: any;
      try {
        parsed = JSON.parse(text);
      } catch {
        throw new Error('The selected file is not valid JSON.');
      }

      // Accept either an array directly, or { products: [...] }
      const productsArray = Array.isArray(parsed) ? parsed : parsed.products;
      if (!Array.isArray(productsArray)) {
        throw new Error('JSON must be an array of products or { products: [...] }');
      }

      const result = await productService.importProducts(productsArray, undefined, tenantId);
      setImportResult(result);
      await fetchProducts();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setImporting(false);
      // Reset file input so the same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm(`Delete ALL products? This cannot be undone.`)) return;
    setDeletingAll(true);
    setError(null);
    setSuccessMessage(null);
    setImportResult(null);
    try {
      const deleted = await productService.deleteAllProducts(tenantId);
      setProducts([]);
      setSelectedProduct(null);
      setSuccessMessage(`${deleted} product(s) deleted.`);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDeletingAll(false);
    }
  };

  const handleDelete = async (product: Product) => {
    if (!window.confirm(`Delete "${product.name}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await productService.deleteProduct(product.productId, tenantId);
      setSelectedProduct(null);
      await fetchProducts();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  // Derive unique classifications from loaded products for filter dropdown
  const classifications = Array.from(new Set(products.map(p => p.classification).filter(Boolean))).sort();

  const bannerBase = 'relative px-4 py-3 rounded-lg mb-4 text-body flex items-start justify-between gap-3';
  const dismissBtn = 'flex-shrink-0 text-lg leading-none opacity-60 hover:opacity-100 transition-opacity duration-fast';

  return (
    <div className="min-h-screen bg-surface-app">
      <TopBar
        tenantName={currentTenant?.name || 'Workspace'}
        userEmail={currentUser?.email || ''}
      />

      <div className="px-8 py-6 max-w-screen-xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button type="button" onClick={() => navigate('/workflows')}
              className="text-label text-gray-500 hover:text-gray-900 transition-colors duration-fast">
              ← Workflows
            </button>
            <h1 className="text-3xl font-semibold text-gray-900 tracking-tight">Product Library</h1>
          </div>
          <div className="flex items-center gap-3">
            {products.length > 0 && (
              <button type="button" onClick={handleDeleteAll} disabled={deletingAll || importing}
                className="px-4 py-2.5 bg-error-light text-error border border-error/30 rounded-md text-label font-medium hover:bg-red-100 transition-colors duration-fast disabled:opacity-50">
                {deletingAll ? 'Deleting…' : 'Delete All'}
              </button>
            )}
            <button type="button" onClick={() => fileInputRef.current?.click()} disabled={importing}
              className="px-4 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-md text-label font-semibold transition-colors duration-fast shadow-sm disabled:opacity-50">
              {importing ? 'Importing…' : 'Import Products'}
            </button>
            <input ref={fileInputRef} type="file" accept=".json" aria-label="Import products JSON file"
              className="hidden" onChange={handleImportFile} />
          </div>
        </div>

        {/* Import result banner */}
        {importResult && (
          <div className={`${bannerBase} ${importResult.errors.length > 0 ? 'bg-warning-light text-warning-dark border border-warning/30' : 'bg-success-light text-success-dark border border-success/30'}`}>
            <div>
              <strong>Import complete:</strong> {importResult.imported} product{importResult.imported !== 1 ? 's' : ''} imported.
              {importResult.errors.length > 0 && (
                <ul className="list-disc list-inside mt-1 text-label">
                  {importResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
            </div>
            <button type="button" className={dismissBtn} onClick={() => setImportResult(null)}>×</button>
          </div>
        )}

        {/* Success banner */}
        {successMessage && (
          <div className={`${bannerBase} bg-success-light text-success-dark border border-success/30`}>
            <span>{successMessage}</span>
            <button type="button" className={dismissBtn} onClick={() => setSuccessMessage(null)}>×</button>
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div className={`${bannerBase} bg-error-light text-error-dark border border-error/30`}>
            <span>{error}</span>
            <button type="button" className={dismissBtn} onClick={() => setError(null)}>×</button>
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-3 items-center mb-5">
          <input type="text" placeholder="Search products…" value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-md text-body bg-white outline-none focus:border-blue-400 focus:shadow-focus transition-[border-color,box-shadow] duration-fast placeholder:text-gray-400" />
          <select aria-label="Filter by classification" value={classificationFilter}
            onChange={e => setClassificationFilter(e.target.value)}
            className="px-4 py-2.5 border border-gray-300 rounded-md text-body bg-white outline-none focus:border-blue-400 transition-colors duration-fast select-input">
            <option value="">All types</option>
            {classifications.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <span className="text-label text-gray-500 whitespace-nowrap">
            {products.length} product{products.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Main layout */}
        <div className="flex gap-6 min-h-0">
          {/* Product grid */}
          <div className="flex-1 min-w-0">
            {loading ? (
              <div className="py-16 text-center text-body text-gray-400">Loading products…</div>
            ) : products.length === 0 ? (
              <div className="py-16 text-center">
                <div className="text-5xl mb-4">📦</div>
                <p className="text-body text-gray-500 mb-5">
                  {searchQuery || classificationFilter ? 'No products match your filters.' : 'No products imported yet.'}
                </p>
                {!searchQuery && !classificationFilter && (
                  <button type="button" onClick={() => fileInputRef.current?.click()}
                    className="px-5 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-md text-label font-semibold transition-colors duration-fast">
                    Import Products
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {products.map(product => {
                  const isSelected = selectedProduct?.productId === product.productId;
                  return (
                    <button key={product.productId} type="button" onClick={() => setSelectedProduct(product)}
                      className={`text-left rounded-lg border-2 overflow-hidden transition-all duration-base shadow-xs ${
                        isSelected ? 'border-blue-500 shadow-focus' : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                      } bg-white`}>
                      <div className="aspect-square bg-gray-100 overflow-hidden flex items-center justify-center">
                        {product.primaryImage
                          ? <img src={product.primaryImage} alt={product.name} loading="lazy" className="w-full h-full object-cover" />
                          : <span className="text-4xl">📦</span>
                        }
                      </div>
                      <div className="p-3">
                        <div className="text-label font-semibold text-gray-900 truncate">{product.name}</div>
                        {product.variant && <div className="text-meta text-gray-500 truncate">{product.variant}</div>}
                        {product.classification && (
                          <span className="mt-1.5 inline-block px-2 py-0.5 bg-blue-50 text-blue-700 text-meta font-medium rounded-full">{product.classification}</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Details panel */}
          {selectedProduct && (
            <div className="w-72 flex-shrink-0 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <h2 className="text-body font-semibold text-gray-900">Details</h2>
                <button type="button" onClick={() => setSelectedProduct(null)}
                  className="w-7 h-7 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors duration-fast text-lg leading-none">×</button>
              </div>
              {selectedProduct.primaryImage && (
                <div className="aspect-square bg-gray-100 overflow-hidden">
                  <img src={selectedProduct.primaryImage} alt={selectedProduct.name} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-4 overflow-y-auto flex-1">
                {[
                  ['Name', selectedProduct.name],
                  ['Classification', selectedProduct.classification],
                  ['Variant', selectedProduct.variant],
                  ['SKU', selectedProduct.sku],
                  ['Price', selectedProduct.price ? `${selectedProduct.price}${selectedProduct.currency ? ` ${selectedProduct.currency}` : ''}` : null],
                  ['Availability', selectedProduct.availability],
                  ['Imported', new Date(selectedProduct.importedAt).toLocaleDateString()],
                ].filter(([, v]) => v).map(([lbl, val]) => (
                  <div key={lbl as string} className="flex gap-3 py-2 border-b border-gray-100 last:border-0">
                    <span className="text-meta font-medium text-gray-500 w-24 flex-shrink-0">{lbl}</span>
                    <span className="text-label text-gray-900">{val as string}</span>
                  </div>
                ))}
                {selectedProduct.description && (
                  <div className="flex gap-3 py-2 border-b border-gray-100">
                    <span className="text-meta font-medium text-gray-500 w-24 flex-shrink-0">Description</span>
                    <span className="text-label text-gray-700 leading-relaxed">{selectedProduct.description}</span>
                  </div>
                )}
                {selectedProduct.sourceUrl && (
                  <div className="flex gap-3 py-2 border-b border-gray-100">
                    <span className="text-meta font-medium text-gray-500 w-24 flex-shrink-0">Source</span>
                    <a href={selectedProduct.sourceUrl} target="_blank" rel="noreferrer"
                      className="text-label text-blue-500 hover:text-blue-600 hover:underline">View original ↗</a>
                  </div>
                )}
                <div className="pt-4">
                  <button type="button" onClick={() => handleDelete(selectedProduct)} disabled={deleting}
                    className="w-full px-4 py-2.5 bg-error-light text-error border border-error/30 rounded-md text-label font-medium hover:bg-red-100 transition-colors duration-fast disabled:opacity-50">
                    {deleting ? 'Deleting…' : 'Delete Product'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductLibrary;

